import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { LoginService } from './services/login.service';
import { BehaviorSubject, filter } from 'rxjs';
import { HubConnection } from '@microsoft/signalr';
import { convertDates } from './interceptor/DateInterceptor';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private hubUrl: string = environment.APIEndpoint.replace('/api/v1/', '') +  '/dashboardhub';
  connection: any;

  public static readonly delayTime: number = 10000;

  public onPage = false;

  private connectionReady$ = new BehaviorSubject<signalR.HubConnection | null>(null);
  readonly whenConnected$ = this.connectionReady$.pipe(
    filter((conn): conn is signalR.HubConnection => conn !== null)
  );

  constructor(private loginService: LoginService) { }

  public async initiateSignalrConnection(): Promise<void> {
    if (this.onPage && this.connection == null){
      try {
        const token = this.loginService.AccessToken?.authToken;

        if (!token) {
          console.warn('No auth token available, skipping SignalR init');
          return;
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(this.hubUrl, {
              accessTokenFactory: () => token
            })
            .configureLogging(signalR.LogLevel.Warning)
            .withAutomaticReconnect(new RetryPolicy(this))
            .build();

        await this.connection.start({ withCredentials: false });

        this.connectionReady$.next(this.connection);

        console.log(`SignalR connection success! connectionId: ${this.connection.connectionId}`);
      }
      catch (error) {
        console.log(`SignalR connection error: ${error}. Retry in 10.`);

        this.connection = null

        setTimeout(() => this.initiateSignalrConnection(), SignalrService.delayTime)
      }
    }
  }
}

class RetryPolicy implements signalR.IRetryPolicy{
  constructor(private service: SignalrService) {}

  nextRetryDelayInMilliseconds(_: signalR.RetryContext): number | null {
    if (!this.service.onPage) {
      return null;
    }

    return SignalrService.delayTime;
  }
}

export function registerHandler<T>(
  connection: HubConnection,
  methodName: string,
  callback: (data: T) => void,
  reconnectedcallback: (connectionId?: string | undefined) => void
): void;

export function registerHandler<T, T2>(
  connection: HubConnection,
  methodName: string,
  callback: (data: T, data2: T2) => void,
  reconnectedcallback: (connectionId?: string | undefined) => void
): void;

export function registerHandler<T, T2, T3>(
  connection: HubConnection,
  methodName: string,
  callback: (data: T, data2: T2, data3: T3) => void,
  reconnectedcallback: (connectionId?: string | undefined) => void
): void;

// Implementation signature must be broad enough to cover all overloads
export function registerHandler<T, T2 = never, T3 = never>(
  connection: HubConnection,
  methodName: string,
  callback: (data: T, data2?: T2, data3?: T3) => void,
  reconnectedcallback: (connectionId?: string | undefined) => void
): void {
  connection.on(methodName, (raw: T, raw2?: T2, raw3?: T3) => callback(convertDates(raw), raw2, raw3));
  connection.onreconnected(reconnectedcallback)
}

